'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { FIT } from './styles'

// Resolve a media URL to what <img>/<video> should load. Local optimized assets
// (the Featured/static seed, e.g. "assets/explore/foo.webp") are served from
// /assets/...; everything else gets the same host swap the grid Card applies
// (item.imageUrl already had URL_1 -> URL_2 applied by the page mapper).
export const editUrl = (url) => {
  const u = url || ''
  if (u.startsWith('assets/')) return `/${u}`
  return u.replace('bucket.promptchan.ai', 'bucket.aicloudnetservices.com')
}

// Fraction of a slide that must be visible before its clip starts playing. Kept
// below 0.5 so playback begins DURING the swipe — the incoming reel is already
// rolling before it snaps to center, and the outgoing reel keeps playing until
// it's mostly gone (no freeze-frame flash mid-transition). Mirrors the /generate
// feed's IntersectionObserver-driven playback (GenerateFeed's FeedVideo).
const PLAY_RATIO = 0.35

// Playback is driven by REAL visibility (an IntersectionObserver on the element),
// not by the rounded active index — so the next clip starts as you scroll toward
// it instead of stalling at the poster until the scroll settles. src is deferred
// until within the preload window so a long feed doesn't fetch every <video>; the
// active slide and its immediate neighbours buffer ahead (`preload="auto"`) so a
// swipe never waits on a fetch. `active` still gates audio + rewind-on-leave.
export default function ImmersiveVideo({
  item,
  active,
  near,
  muted,
  paused,
  preload,
  videoRef,
}) {
  // Attach the source when within the preload window (active is always in-window)
  // and drop it when far away to free memory.
  const shouldLoad = preload || active
  const [mediaReady, setMediaReady] = useState(false)

  // A virtualized slide can remain mounted while its source is released. Reset
  // readiness so reattaching that source fades in again instead of flashing in.
  useEffect(() => {
    if (!shouldLoad) setMediaReady(false)
  }, [shouldLoad])

  // Latest control values, read by the (set-up-once) IntersectionObserver callback
  // and by apply() without re-creating the observer on every prop change.
  const stateRef = useRef({ active, muted, paused })
  stateRef.current = { active, muted, paused }
  const visibleRef = useRef(false)

  // Reconcile the element to the current (visibility + control) state. Set `muted`
  // BEFORE play() because a fresh <video> defaults to muted=false and unmuted
  // autoplay is blocked. Only the centered (active) clip carries audio when the
  // feed is unmuted; any other clip that's briefly co-visible during a transition
  // stays muted so two reels never play sound at once.
  const apply = useCallback(() => {
    const el = videoRef.current
    if (!el) return
    const { active: a, muted: m, paused: p } = stateRef.current
    el.muted = m || !a
    if (visibleRef.current && !p) el.play?.().catch(() => {})
    else el.pause?.()
  }, [videoRef])

  // Visibility-driven play/pause. Start once the slide crosses PLAY_RATIO; rewind
  // to the top only once it's fully scrolled away (so revisiting restarts from the
  // beginning without freezing a still-visible outgoing clip at frame 0).
  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.intersectionRatio >= PLAY_RATIO
        if (entry.intersectionRatio < 0.02) {
          try {
            el.currentTime = 0
          } catch {
            /* ignore */
          }
        }
        apply()
      },
      { threshold: [0.02, PLAY_RATIO] },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [apply, shouldLoad])

  // Re-apply on control changes (tap-pause, active hand-off, mute toggle). The
  // actual unmute on tap is done synchronously in the button handler
  // (Slide.toggleMute) because iOS ignores an unmute that comes from an effect
  // rather than from the user-gesture call stack.
  useEffect(() => {
    apply()
  }, [active, muted, paused, apply])

  return (
    <video
      ref={videoRef}
      src={shouldLoad ? editUrl(item.imageUrl) : undefined}
      poster={editUrl(item.videoThumbnail || item.original_image_url)}
      // Active + immediate neighbours buffer ahead so a swipe never waits on a
      // fetch; farther preloaded slides only grab metadata + first frame (cheap).
      preload={active || near ? 'auto' : 'metadata'}
      className={`${FIT} transition-opacity duration-500 ease-out motion-reduce:transition-none ${
        mediaReady ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ touchAction: 'manipulation' }}
      onLoadedData={() => setMediaReady(true)}
      loop
      playsInline
    />
  )
}
